import * as React from 'react';

/**
 * The member app's connectivity state, and the two things it is careful not to be
 * (`MEM-043` AC-3, AC-4).
 *
 * ## It is not a claim that the server is reachable
 *
 * `navigator.onLine` is false only when the device knows it has no network at all. True means
 * "there is an interface up", which at a venue means the captive portal you have not signed into
 * yet. So `offline` here is a **one-way fact**: false is worth showing, true is worth nothing,
 * and no screen may read `online` as permission to treat anything as current. What is drawn from
 * this is a notice saying the app cannot reach H2HSpark — never a green tick saying it can.
 *
 * ## It is not a queue, and this file is where one would have to live
 *
 * `MEM-043` AC-4 — no offline check-in, no offline connection queue. The obvious next commit
 * after a connectivity hook is the one that listens for `online` and retries what failed while it
 * was false, and that commit is the bug: a check-in replayed an hour later admits somebody who
 * was not there, and a connection replayed after the room emptied records a conversation nobody
 * had. `EVT-031` accepts the connectivity risk and mitigates it with paper.
 *
 * So there is **no `online` listener that retries anything** here. The subscription below exists
 * to redraw a notice, and the redraw is all it does — no refetch, no invalidation, no flush. A
 * member who was offline finds out by asking again, which is a tap they choose to make while
 * standing where they can see whether it worked.
 *
 * `the-shell-opens-offline.test.ts` scans this file for the tokens a queue would need.
 */

/**
 * Whether the browser is certain there is no network.
 *
 * Separate from the hook so the rule — and specifically the "unknown means online" default — can
 * be asserted without a document. A browser with no `navigator.onLine` (and jsdom before the
 * property is defined) reports online, because an unproven suspicion of offline is exactly the
 * thing that would put a false notice over a working app.
 */
export function isKnownOffline(candidate: { readonly onLine?: unknown } | undefined): boolean {
  if (candidate === undefined) return false;
  return candidate.onLine === false;
}

/**
 * Subscribes to the browser's own connectivity events and reports the state.
 *
 * Both events are listened to because the notice has to come *down* as well as up — a stuck
 * "you are offline" over a working app is the same class of lie as stale data presented as
 * current, pointing the other way.
 */
export function useConnectivity(): { readonly offline: boolean } {
  const [offline, setOffline] = React.useState(() =>
    typeof navigator === 'undefined' ? false : isKnownOffline(navigator),
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Read once on mount as well as on the events: the state can have changed between the render
    // that seeded it and this effect, and the browser does not re-fire the event to catch us up.
    setOffline(isKnownOffline(navigator));

    const update = (): void => {
      setOffline(isKnownOffline(navigator));
    };
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return { offline };
}

/** The notice's heading. Names the product, because "you are offline" is the device's claim. */
export const OFFLINE_NOTICE_TITLE = 'You are offline';

/**
 * The body, and every word of it is load-bearing.
 *
 * It says what is not working ("cannot reach H2HSpark"), what the member is looking at ("what is
 * on this screen may be out of date"), and — the part `MEM-043` AC-4 needs — that nothing they do
 * now is being saved for later. A member who believes a scan will go through when the signal
 * returns will stop scanning, and the connection is lost with nobody aware of it.
 */
export const OFFLINE_NOTICE_BODY =
  'H2HSpark cannot be reached, so what is on this screen may be out of date. Check-ins and ' +
  'connections are not saved while you are offline — try again once you have signal.';
